import {
  AllTemplatesForBuilderDocument,
  GetVersionsDocument,
  TemplateTypeEnum,
  useAllTemplatesForBuilderQuery,
  useCloneQuerySummaryMutation,
} from '@/__generated__/graphql';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { useSnackBarMessage } from './utils';
import DownloadIcon from '@mui/icons-material/Download';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { abyssColors } from '../../theme/colors';
import { GenericDialog } from '../shared/GenericDialog';
import { StructuresList } from './StructuresList';
import { TemplateForm } from './TemplateForm';
import { FormTypes } from './constants';
import { querySummaries, selectedQuerySummary, selectedTemplateId } from './state';
import { PageLoader } from '../shared/PageLoader';

type QuerySummaryTemplateProps = {
  structureId: string;
  structureName: string;
};

export const QuerySummaryTemplate = ({ structureId, structureName }: QuerySummaryTemplateProps) => {
  const [allQuerySummaries, setAllSummaryQueries] = useAtom(querySummaries);
  const setSelectedQuerySummary = useSetAtom(selectedQuerySummary);
  const setTemplateId = useSetAtom(selectedTemplateId);
  const [emptyForm, setEmptyForm] = useState<boolean>(false);
  const [showStructuresList, setShowStructuresList] = useState<boolean>(false);
  const [selectedStructureId, setSelectedStructureId] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [cloneQuerySummary, { loading }] = useCloneQuerySummaryMutation();

  const { data, refetch } = useAllTemplatesForBuilderQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.Assembly,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (data?.allTemplates && data.allTemplates.length > 0) {
      setTemplateId(data.allTemplates.at(0)?.id);
      setAllSummaryQueries(data.allTemplates.at(0)?.querySummary ?? []);
      setSelectedQuerySummary(data.allTemplates.at(0)?.querySummary?.at(0));
    }
  }, [data?.allTemplates, setAllSummaryQueries, setSelectedQuerySummary, setTemplateId]);

  const handlePlatformClick = useCallback(
    (id: string) => {
      if (id === structureId) {
        showSnackBar(`You have already selected ${structureName}`, 'info');
      } else {
        setSelectedStructureId(id);
        setIsOpen(true);
      }
    },
    [showSnackBar, structureId, structureName]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const addTemplateFieldsToStructure = useCallback(async () => {
    await cloneQuerySummary({
      variables: {
        input: {
          type: TemplateTypeEnum.Assembly,
          name: 'Pipes',
          sourceStructureId: selectedStructureId,
          targetStructureId: structureId,
        },
      },
      refetchQueries: [AllTemplatesForBuilderDocument, GetVersionsDocument],
      onCompleted: () => {
        handleClose();
        showSnackBar(`Query Summary has been cloned to ${structureName} successfully`, 'success');
      },
      onError: (error) => {
        console.error(error);
        handleClose();
        showSnackBar(`${error.message}`, 'error');
      },
    });
  }, [
    cloneQuerySummary,
    selectedStructureId,
    structureId,
    handleClose,
    showSnackBar,
    structureName,
  ]);

  const downloadJson = useCallback(() => {
    refetch().then(({ data: allTemplatesData }) => {
      const querySummaryData = allTemplatesData?.allTemplates.at(0)?.querySummary;
      const jsonData = JSON.stringify(querySummaryData, undefined, 2);
      const jsonBlob = new Blob([jsonData], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(jsonBlob);
      const fileName = `${structureName}-query-summary-${Date.now().toString()}.json`;
      link.download = fileName;
      document.body.append(link);
      link.click();
      link.remove();
    });
  }, [refetch, structureName]);

  return (
    <Stack>
      <Stack
        direction="row"
        gap={2}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          pt: 4,
          alignItems: 'center',
        }}
      >
        {showStructuresList && (
          <Typography variant="h6">Select a platform to copy Query Summary from</Typography>
        )}

        <Stack
          direction="row"
          spacing={1}
          sx={{
            ml: 'auto',
            flexShrink: 0,
          }}
        >
          {showStructuresList && (
            <Button
              startIcon={<VisibilityIcon />}
              variant="contained"
              onClick={() => {
                setShowStructuresList(false);
              }}
              sx={containedButtonStyle}
            >
              View Query Summary
            </Button>
          )}
          <Button
            startIcon={<FileCopyIcon />}
            variant="contained"
            sx={containedButtonStyle}
            onClick={() => {
              setShowStructuresList(true);
            }}
          >
            Copy Query Summary From Another Platform
          </Button>
          {!showStructuresList && (
            <Button
              startIcon={<DownloadIcon />}
              variant="contained"
              onClick={() => downloadJson()}
              sx={containedButtonStyle}
            >
              Download JSON
            </Button>
          )}
        </Stack>
      </Stack>

      {!showStructuresList && allQuerySummaries && (
        <TemplateForm
          handleEmptyForm={(value) => setEmptyForm(value)}
          emptyForm={emptyForm}
          currentTemplateType={FormTypes.QuerySummary.toString()}
          structureId={structureId}
        />
      )}
      {!showStructuresList && !allQuerySummaries && <PageLoader />}
      {showStructuresList && (
        <Box>
          <StructuresList onPlatformClick={handlePlatformClick} drawerOpen />
        </Box>
      )}
      <GenericDialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title="Add Query Summary"
        content={
          <Typography fontSize="1.4rem" color={abyssColors.primary[500]}>
            Are you sure you want to clone the query summary from this deployment?
          </Typography>
        }
        actions={
          <>
            <Button variant="text" onClick={() => setIsOpen(false)} sx={textButtonStyle}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => addTemplateFieldsToStructure()}
              sx={containedButtonStyle}
              disabled={loading}
            >
              Confirm
            </Button>
          </>
        }
        actionsStyles={{ mx: 3, pb: 3 }}
      />
    </Stack>
  );
};
